import Context from "shared/util/Context";
import uniqBy from "lodash/uniqBy";
import findIndex from "lodash/findIndex";
import isEqual from "lodash/isEqual";

import GetUserIdFromWindowLocation from "shared/util/GetUserIdFromWindowLocation";
import Report, { TYPES } from "shared/util/Report";
import PhoneVerificationModal from "shared/components/ModalPhoneVerification";
import _str from "shared/util/_str";
import _strf from "shared/util/_strf";
import * as ProfileActions from "shared/util/ProfileActions";

import * as Api from "apps/messenger/util/web";

import {
	isConversationActive,
	isStandalonePage,
	closeStandalonePage,
	generateTemporaryMessageId,
	generateTimestamp,
	getLastMessageFromUser,
	setupPageVisibilityListeners,
	normalizeConversationData,

	normalizeMessengerState,
	denormalizeMessengerState,
	requestNotificationPermission,
	notify,
	showPhotoBlock,
	showMessagePledge,
} from "apps/messenger/util/helpers";

import {
	WINDOW_STATE,
	ERROR_MESSAGE_DURATION,
	ERROR_MESSAGE_DEFAULT,
	SYSTEM_USERID,
	MESSAGES_LIMIT,
	SEND_TRIGGER_TYPE,
} from "apps/messenger/util/consts";

import {
	OKEVENT,

	subscribeToOkEvent,
} from "shared/util/OkEventHelpers";

import {
	isMobile,
} from "shared/util/EnvironmentHelpers";

// action types
// -----------------------------------------------------------------------------

export const GET_MESSENGER_STATE_START = "MESSAGING.GET_MESSENGER_STATE_START";
export const GET_MESSENGER_STATE_SUCCESS = "MESSAGING.GET_MESSENGER_STATE_SUCCESS";
export const GET_MESSENGER_STATE_FAIL = "MESSAGING.GET_MESSENGER_STATE_FAIL";

export const UPDATE_DRAFT = "MESSAGING.UPDATE_DRAFT";
export const UPDATE_DRAFT_FAIL = "MESSAGING.UPDATE_DRAFT_FAIL";

export const SEND_MESSAGE_START = "MESSAGING.SEND_MESSAGE_START";
export const SEND_MESSAGE_SUCCESS = "MESSAGING.SEND_MESSAGE_SUCCESS";
export const SEND_MESSAGE_FAIL = "MESSAGING.SEND_MESSAGE_FAIL";
export const SEND_MESSAGE_FAIL_REMOVE_ERROR = "MESSAGING.SEND_MESSAGE_FAIL_REMOVE_ERROR";

export const LOAD_MESSAGES_START = "MESSAGING.LOAD_MESSAGES_START";
export const LOAD_MESSAGES_SUCCESS = "MESSAGING.LOAD_MESSAGES_SUCCESS";
export const LOAD_MESSAGES_FAIL = "MESSAGING.LOAD_MESSAGES_FAIL";

export const ADD_CONVERSATION = "MESSAGING.ADD_CONVERSATION";
export const OPEN_CONVERSATION = "MESSAGING.OPEN_CONVERSATION";
export const MINIMIZE_CONVERSATION = "MESSAGING.MINIMIZE_CONVERSATION";
export const CLOSE_CONVERSATION = "MESSAGING.CLOSE_CONVERSATION";

export const ADD_MESSAGE = "MESSAGING.ADD_MESSAGE";
export const ADD_READ_RECEIPT = "MESSAGING.ADD_READ_RECEIPT";

export const MARK_MESSAGE_READ = "MESSAGING.MARK_MESSAGE_READ";

export const UNBLOCK_USER = "MESSAGING.UNBLOCK_USER";
export const DISMISS_CONTENT_WARNING = "MESSAGING.DISMISS_CONTENT_WARNING";

export const PROFILE_LINK_ACCEPT = "MESSAGING.PROFILE_LINK_ACCEPT";
export const PROFILE_LINK_REJECT = "MESSAGING.PROFILE_LINK_REJECT";

export const MOVE_TO_SMS_START = "MESSAGING.MOVE_TO_SMS_START";
export const MOVE_TO_SMS_SUCCESS = "MESSAGING.MOVE_TO_SMS_SUCCESS";
export const MOVE_TO_SMS_FAIL = "MESSAGING.MOVE_TO_SMS_FAIL";

export const SET_PAGE_VISIBLE = "MESSAGING.SET_PAGE_VISIBLE";
export const SET_PAGE_HIDDEN = "MESSAGING.SET_PAGE_HIDDEN";

export const SET_PREFERRED_SEND_TRIGGER = "MESSAGING.SET_PREFERRED_SEND_TRIGGER";
export const GRADUATE_MESSAGE_PLEDGE = "MESSAGING.GRADUATE_MESSAGE_PLEDGE";


// initial state
// -----------------------------------------------------------------------------

const INITIAL_STATE = {
	conversationOrder: [],
	conversationData: {},
	needsPhoneVerification: false,
	messagePledge: {
		understands: true,
		text: {},
	},
	preferredSendTrigger: SEND_TRIGGER_TYPE.EnterToSend,

	pageVisible: false,
	stateLoading: false,
	stateLoadingError: null,
	draftsFull: false,
};


// reducer
// -----------------------------------------------------------------------------

export default function reducer(state = INITIAL_STATE, action) {
	switch (action.type) {
		case GET_MESSENGER_STATE_START: {
			return {
				...state,
				stateLoading: true,
				stateLoadingError: null,
			};
		}

		case GET_MESSENGER_STATE_SUCCESS: {
			return {
				...state,
				...action.payload,
				stateLoading: false,
			};
		}

		case GET_MESSENGER_STATE_FAIL: {
			return {
				...state,
				stateLoading: false,
				stateLoadingError: action.payload.error,
			};
		}

		case ADD_CONVERSATION: {
			const userid = action.payload.userid;

			// Update conversation state and set to overflowed by default.
			const updatedConversationData = {
				...action.payload.conversation,
				state: WINDOW_STATE.Overflow,
			};

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[userid]: updatedConversationData,
				},
			};
		}

		case OPEN_CONVERSATION: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Move conversation to first position in order,
			// (This also adds it in case it's not there at all)
			const conversationOrderWithoutConversation = state.conversationOrder
				.filter((userid) => userid !== conversation.userid);
			const updatedConversationOrder = [
				conversation.userid,
				...conversationOrderWithoutConversation,
			];

			// Set the conversation to be open state.
			const updatedConversation = {
				...conversation,
				state: WINDOW_STATE.Open,
				likedThisSession: action.payload.likedThisSession || conversation.likedThisSession,
			};

			// Update like object of targetUser based on likedThisSession.
			let targetUserLikes = (updatedConversation.fields && updatedConversation.fields.likes) || {};

			if (!!targetUserLikes.they_like && updatedConversation.likedThisSession) {
				targetUserLikes = {
					...targetUserLikes,
					mutual_like: 1,
					mutual_like_vote: 1,
				};

				updatedConversation.fields = {
					...updatedConversation.fields,
					likes: targetUserLikes,
				};
			}




			// Update the conversation.
			const updatedConversationData = {
				...state.conversationData,
				[conversation.userid]: updatedConversation,
			};

			// If we just opened an overflowed conversation,
			// Check all other conversations, and overflow them as needed.
			if (conversation.state === WINDOW_STATE.Overflow) {
				conversationOrderWithoutConversation.forEach((userid) => {
					const thisConversation = updatedConversationData[userid];

					// This conversation used to be minimized or open.
					if (thisConversation.state !== WINDOW_STATE.Overflow) {
						const updatedState = WINDOW_STATE.Overflow;

						updatedConversationData[userid] = {
							...thisConversation,
							state: updatedState,
						};
					}
				});
			}

			return {
				...state,
				conversationOrder: updatedConversationOrder,
				conversationData: updatedConversationData,
			};
		}

		case MINIMIZE_CONVERSATION: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Set state to minimized.
			const updatedState = WINDOW_STATE.Minimized;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						state: updatedState,
					},
				},
			};
		}

		case CLOSE_CONVERSATION: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Removes the conversation from conversationOrder.
			const updatedConversationOrder = state.conversationOrder
				.filter((tid) => tid !== conversation.userid);

			// Set the state to overflow, just in case
			// we want to retrieve this data later.
			const updatedConversation = {
				...conversation,
				state: WINDOW_STATE.Overflow,
			};

			// If this conversation used to be open,
			// Set the next item in the order as minimized.
			const nextActiveConversationId = updatedConversationOrder[0];
			const nextActiveConversation = state.conversationData[nextActiveConversationId];
			const updatedNextActiveConversation = conversation.state !== WINDOW_STATE.Overflow ? {
				...nextActiveConversation,
				state: WINDOW_STATE.Minimized,
			} : nextActiveConversation;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: updatedConversation,
					[nextActiveConversationId]: updatedNextActiveConversation,
				},
				conversationOrder: updatedConversationOrder,
			};
		}

		case UPDATE_DRAFT: {
			const conversation = state.conversationData[action.payload.userid];

			// Update the draft.
			const updatedDraft = {
				...(conversation.draft || {}),
				body: action.payload.body,
				profileComment: action.payload.profileComment,
			};

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						draft: updatedDraft,
					},
				},
			};
		}

		case UPDATE_DRAFT_FAIL: {
			const updatedDraftsFull = action.payload.draftsFull;

			return {
				...state,
				draftsFull: updatedDraftsFull,
			};
		}

		case SEND_MESSAGE_START: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Mock message to serve as a placeholder
			// while we send to the server.
			const mockMessage = {
				...action.payload.message,
				isSending: true,
			};

			// Update the list of messages.
			const updatedMessages = [
				...conversation.messages,
				mockMessage,
			];

			// Update the total message count.
			const updatedTotal = conversation.totalMessages + 1;

			// Update the nways count.
			let updatedNway = conversation.nway || 0;

			// If the last message wasn't from the same person, increment nways.
			const previousLastMessage = conversation.messages[conversation.messages.length - 1];

			if (!previousLastMessage || previousLastMessage.from !== mockMessage.from) {
				updatedNway += 1;
			}

			// Update the sending state.
			const updatedSendingState = true;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						isSending: updatedSendingState,
						messages: updatedMessages,
						nway: updatedNway,
						totalMessages: updatedTotal,
					},
				},
			};
		}

		case SEND_MESSAGE_SUCCESS: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Update the temp message.
			const updatedMessages = uniqBy(
				conversation.messages.map((message) => {
					if (message.tmpId === action.payload.tmpId) {
						return {
							...message,
							id: action.payload.msgid,
							isSending: false,
						};
					}

					return message;
				}),
				"id",
			);

			// Update user instructions.
			const updatedUserInstructions = action.payload.userInstructions;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						messages: updatedMessages,
						userInstructions: updatedUserInstructions,
					},
				},
			};
		}

		case SEND_MESSAGE_FAIL: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Update the error.
			const updatedError = action.payload.error;

			// Remove the message we were trying to send.
			const messageIndex = findIndex(
				conversation.messages,
				(message) => message.tmpId === action.payload.tmpId,
			);
			const messagesBefore = conversation.messages.slice(0, messageIndex);
			const messagesAfter = conversation.messages.slice(messageIndex + 1);
			const restoreMessages = [...messagesBefore, ...messagesAfter];

			// Restore the old sending state.
			const restoreSendingState = false;

			// Recalculate nways, with the message removed.
			let correctedNway = action.payload.nwayBeforeSend;

			// (Basically we have to go through the newer messages, and recheck how those affect nway)
			messagesAfter.forEach((message, index) => {
				const previousMessage = restoreMessages[messageIndex - 1 + index];

				if (!previousMessage || previousMessage.from !== message.from) {
					correctedNway += 1;
				}
			});

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						error: updatedError,
						isSending: restoreSendingState,
						messages: restoreMessages,
						nway: correctedNway,
					},
				},
			};
		}

		case SEND_MESSAGE_FAIL_REMOVE_ERROR: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Update the error.
			const updatedError = null;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						error: updatedError,
					},
				},
			};
		}

		case LOAD_MESSAGES_START: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Update the loading state.
			const updatedLoadingMessages = true;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						loadingMessages: updatedLoadingMessages,
					},
				},
			};
		}

		case LOAD_MESSAGES_SUCCESS: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Update the messages list.
			const updatedMessages = uniqBy([
				...action.payload.messages,
				...conversation.messages,
			], "id");

			// Update the loading state.
			const updatedLoadingMessages = false;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						loadingMessages: updatedLoadingMessages,
						messages: updatedMessages,
					},
				},
			};
		}

		case LOAD_MESSAGES_FAIL: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Update the loading state.
			const updatedLoadingMessages = false;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						loadingMessages: updatedLoadingMessages,
					},
				},
			};
		}

		case ADD_MESSAGE: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Update the message list.
			const updatedMessages = uniqBy([
				...conversation.messages,
				action.payload.message,
			], "id");

			// Update the total.
			const updatedTotal = conversation.messages.length + 1;

			// Update the nways count.
			let updatedNway = conversation.nway || 0;

			// If the last message wasn't from the same person, increment nways.
			const previousLastMessage = conversation.messages[conversation.messages.length - 1];

			if (!previousLastMessage || previousLastMessage.from !== action.payload.message.from) {
				updatedNway += 1;
			}

			// Update the unread count if necessary.
			const updatedUnreadCount = conversation.state !== WINDOW_STATE.Open
				? conversation.unreadCount + 1
				: conversation.unreadCount;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						messages: updatedMessages,
						totalMessages: updatedTotal,
						nway: updatedNway,
						unreadCount: updatedUnreadCount,
					},
				},
			};
		}

		case ADD_READ_RECEIPT: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Mark this message as read.
			const updatedMessages = conversation.messages.map((message) => {
				if (message.id !== action.payload.msgid) {
					return message;
				}

				return {
					...message,
					read: true,
				};
			});

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						messages: updatedMessages,
					},
				},
			};
		}

		case SET_PAGE_VISIBLE: {
			return {
				...state,
				pageVisible: true,
			};
		}

		case SET_PAGE_HIDDEN: {
			return {
				...state,
				pageVisible: false,
			};
		}

		case MARK_MESSAGE_READ: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Mark this message as having been marked read.
			const updatedMessages = conversation.messages.map((message) => {
				if (message.id !== action.payload.msgid) {
					return message;
				}

				return {
					...message,
					markedRead: true,
				};
			});

			// Reset the unread count to 0.
			const updatedUnreadCount = 0;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						messages: updatedMessages,
						unreadCount: updatedUnreadCount,
					},
				},
			};
		}

		case UNBLOCK_USER: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Set blocked to false.
			const updatedBlockedState = false;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						fields: {
							...conversation.fields,
							blocked: updatedBlockedState,
						},
					},
				},
			};
		}

		case DISMISS_CONTENT_WARNING: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Set showContentWarning to false.
			const updatedShowContentWarning = false;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						showContentWarning: updatedShowContentWarning,
					},
				},
			};
		}

		case MOVE_TO_SMS_START: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Set moveToSMSLoading to true.
			const moveToSMSLoading = true;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						moveToSMSLoading,
					},
				},
			};
		}

		case MOVE_TO_SMS_SUCCESS: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Set showMoveToSMS to false.
			const showMoveToSMS = false;

			// Set needsPhoneVerification to false—we succeeded at moving someone to SMS.
			const needsPhoneVerification = false;

			return {
				...state,
				needsPhoneVerification,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						showMoveToSMS,
					},
				},
			};
		}

		case MOVE_TO_SMS_FAIL: {
			// Old.
			const conversation = state.conversationData[action.payload.userid];

			// Set moveToSMSLoading to false.
			const moveToSMSLoading = false;

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[conversation.userid]: {
						...conversation,
						moveToSMSLoading,
					},
				},
			};
		}

		case PROFILE_LINK_ACCEPT: {
			// Old.
			const conversation = state.conversationData[SYSTEM_USERID];

			// Set the profile link request to accepted.
			const updatedMessages = conversation.messages
				.map(({ profileLinkRequest, ...message }) => {
					if (profileLinkRequest && profileLinkRequest.tuid === action.payload.tuid) {
						return {
							...message,
							profileLinkRequest: {
								...profileLinkRequest,
								isAccepted: true,
							},
						};
					}

					return message;
				});

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[SYSTEM_USERID]: {
						...conversation,
						messages: updatedMessages,
					},
				},
			};
		}

		case PROFILE_LINK_REJECT: {
			// Old.
			const conversation = state.conversationData[SYSTEM_USERID];

			// Set the profile link request to rejected.
			const updatedMessages = conversation.messages
				.map(({ profileLinkRequest, ...message }) => {
					if (profileLinkRequest && profileLinkRequest.tuid === action.payload.tuid) {
						return {
							...message,
							profileLinkRequest: {
								...profileLinkRequest,
								isRejected: true,
							},
						};
					}

					return message;
				});

			return {
				...state,
				conversationData: {
					...state.conversationData,
					[SYSTEM_USERID]: {
						...conversation,
						messages: updatedMessages,
					},
				},
			};
		}

		case SET_PREFERRED_SEND_TRIGGER: {
			// Update the preferred send trigger.
			const updatedPreferredSendTrigger = action.payload.preferredSendTrigger;

			return {
				...state,
				preferredSendTrigger: updatedPreferredSendTrigger,
			};
		}

		case GRADUATE_MESSAGE_PLEDGE: {
			if (!state.messagePledge) {
				return state;
			}

			return {
				...state,
				messagePledge: {
					...state.messagePledge,
					understands: true,
				},
			};
		}

		default: {
			return state;
		}
	}
}


// action exporters
// -----------------------------------------------------------------------------
/* eslint no-use-before-define: [1, 'nofunc'] */

// Loads initial data and sets up event listeners.
export function setupMessenger() {
	return (dispatch) => {
		// Load the initial state.
		dispatch(getMessengerState());

		// Setup all the event listeners.
		dispatch(setupListeners());
	};
}

export function setupConversation() {
	return (dispatch) => {
		// Get the conversation userid from the url.
		const userId = GetUserIdFromWindowLocation();
		dispatch(loadConversation(userId));

		// Setup all the event listeners.
		dispatch(setupListeners());

		// On standalone pages, request notification permissions.
		if (isStandalonePage) {
			requestNotificationPermission();
		}
	};
}

function setupListeners() {
	return (dispatch) => {
		/* Listen for page visible/hidden */
		setupPageVisibilityListeners({
			onPageVisible: () => dispatch(onPageVisible()),
			onPageHidden: () => dispatch(onPageHidden()),
		});

		/* Instant events */
		subscribeToOkEvent(
			OKEVENT.ReceivedMessage,
			(ev) => dispatch(processNewMessage(ev)),
		);
		subscribeToOkEvent(
			OKEVENT.ReceivedReadReceipt,
			(ev) => dispatch(processNewReadReceipt(ev)),
		);
		subscribeToOkEvent(
			OKEVENT.Unmatch,
			(ev) => dispatch(processNewUnmatch(ev)),
		);
		subscribeToOkEvent(
			OKEVENT.Block,
			(ev) => dispatch(processNewBlock(ev)),
		);
	};
}

// Callback for receive message instant event.
function processNewMessage({ message = {}, correspondent = {} } = {}) {
	return (dispatch, getState) => {
		const userid = correspondent.userid;
		const isIncomingMessage = userid === message.from;

		// Don't do anything in this case. It's a lost cause.
		if (!message || !userid) {
			return;
		}

		const state = getState().globalMessaging;

		// Check if the conversation is loaded.
		const conversation = state.conversationData[userid];

		// This conversation wasn't loaded—no reason to open it.
		if (!conversation) {
			return;
		}

		// Add the message.
		dispatch(addMessage(userid, message));

		// Only do these for messages received from someone else.
		if (isIncomingMessage) {
			// Mark the new message as read (as needed).
			dispatch(markConversationMessagesRead(userid));

			// If this page isn't visible, notify the user.
			if (isStandalonePage && !state.pageVisible) {
				const name = conversation.fields.userinfo.displayname;
				notify(_strf("$name sent you a message", { name }));
			}
		}
	};
}

// Callback for receive read receipt instant event.
function processNewReadReceipt(ev) {
	const { msgid, from: userid } = ev;

	return (dispatch, getState) => {
		const conversation = getState().globalMessaging.conversationData[userid];

		// Don't need to do anything here.
		if (userid === Context.get("userid") || !conversation) {
			return;
		}

		// Find the message we want to mark.
		const message = conversation.messages.find(({ id }) => id === msgid);

		// Verify the message exists, and
		// that it's not already read, and
		// that it wasn't sent by this same user.
		if (!message || message.read || message.from === userid) {
			return;
		}

		// Add the read receipt.
		dispatch(addReadReceipt(userid, msgid));
	};
}

// Callback for unmatch instant event.
function processNewUnmatch(ev) {
	const userid = ev.tuid;

	return (dispatch) => {
		if (isStandalonePage) {
			closeStandalonePage();
		} else {
			// Close the conversation
			dispatch(closeConversation(userid));
		}
	};
}

// Callback for block instant event.
function processNewBlock(ev) {
	const userid = ev.tuid;

	return (dispatch) => {
		if (isStandalonePage) {
			closeStandalonePage();
		} else if (process.env.PLATFORM === "desktop") {
			// Close the conversation
			dispatch(closeConversation(userid));
		} else {
			window.location = "/messages";
		}
	};
}

// Get the messenger state from the server.
export function getMessengerState() {
	return (dispatch, getState) => {
		// We're already loading, exit.
		if (getState().globalMessaging.stateLoading) {
			return;
		}

		dispatch({
			type: GET_MESSENGER_STATE_START,
		});

		// Get initial messenger state.
		Api.getMessengerState()
			.then((res) => {
				if (!res || !res.data) {
					dispatch(getMessengerStateFail());
				}

				dispatch(getMessengerStateSuccess(res));
			}).catch((err) => {
				dispatch(getMessengerStateFail(err));
			});
	};
}

// Successfully loaded state.
export function getMessengerStateSuccess({ data }) {
	return (dispatch) => {
		const state = normalizeMessengerState(data);

		dispatch({
			type: GET_MESSENGER_STATE_SUCCESS,
			payload: state,
		});

		/* Weird special cases */
		const userid = GetUserIdFromWindowLocation({
			altQueryParams: ["unmatched_userid"],
		});

		// TODO: also close these when you leave if you didn't interact.
		if (userid) {
			// openwindow=1 query param.
			if (window.location.search.indexOf("openwindow=1") >= 0) {
				dispatch(openConversation(userid));
			}

			// cf=messages query param.
			if (window.location.search.indexOf("cf=messages") >= 0) {
				dispatch(openConversation(userid));
			}

			// unmatched_userid query param, unmatched user.
			if (window.location.search.indexOf("unmatched_userid") >= 0) {
				dispatch(closeConversation(userid));
			}
		}
	};
}

// Failed to load state.
export function getMessengerStateFail(error) {
	return {
		type: GET_MESSENGER_STATE_FAIL,
		payload: {
			error,
		},
	};
}

// Persist the messenger state.
export function persistMessengerState() {
	return (dispatch, getState) => {
		const state = getState().globalMessaging;
		const data = denormalizeMessengerState(state);

		// Persists the state, throttled.
		Api.throttledSetMessengerState(data);
	};
}

// Load messages.
export function loadMessages(userid) {
	return (dispatch, getState) => {
		const conversation = getState().globalMessaging.conversationData[userid];

		// If we're already loading, or already loaded everything, exit.
		const isLoading = conversation.loadingMessages;
		const hasNoMoreMessagesToLoad = (
			conversation.totalMessages === conversation.messages.length
		);

		if (isLoading || hasNoMoreMessagesToLoad) {
			return;
		}

		// Load messages older than our oldest message.
		const before = conversation.messages[0]
			? conversation.messages[0].id
			: undefined;

		// Start loading state.
		dispatch({
			type: LOAD_MESSAGES_START,
			payload: {
				userid,
			},
		});

		const payload = {
			userid,
			before,
			limit: MESSAGES_LIMIT,
		};

		// Load list of messages.
		Api.getMessages(payload)
			.then((res) => dispatch(loadMessagesSuccess(userid, res)))
			.catch((res) => dispatch(loadMessagesFail(userid, res)));
	};
}

// Successfully loaded messages.
export function loadMessagesSuccess(userid, res) {
	return {
		type: LOAD_MESSAGES_SUCCESS,
		payload: {
			userid,
			messages: res.messages,
		},
	};
}

// Failed to load messages.
export function loadMessagesFail(userid, res) {
	return {
		type: LOAD_MESSAGES_FAIL,
		payload: {
			userid,
			...res,
		},
	};
}

// Mark a message as read.
export function markConversationMessagesRead(userid) {
	return (dispatch, getState) => {
		const state = getState().globalMessaging;

		// Page isn't visible, no way they read something, exit.
		if (!state.pageVisible) {
			return;
		}

		// Ignore OkCupid user.
		if (userid === SYSTEM_USERID) {
			return;
		}

		const conversation = state.conversationData[userid];

		// No conversation, or the conversation isn't open, exit.
		if (!conversation || !isConversationActive(conversation)) {
			return;
		}

		const { messages } = conversation;

		// No messages exist in the conversation, exit.
		if (!messages.length) {
			return;
		}

		/* Ok, there might be something to this whole read thing. */

		// Grab the last received message for this conversation.
		const lastReceivedMessage = getLastMessageFromUser(userid, messages);

		// Message was already marked as read, exit.
		if (!lastReceivedMessage || lastReceivedMessage.markedRead) {
			return;
		}

		const msgid = lastReceivedMessage.id;

		// Mark the message as read.
		dispatch({
			type: MARK_MESSAGE_READ,
			payload: {
				userid,
				msgid,
			},
		});

		// Persist it to the sever.
		Api.markMessageRead({ userid, msgid });
	};
}

// Load a conversation asynchronously.
export function loadConversation(userid) {
	return (dispatch) => {
		const payload = {
			userid,
			limit: MESSAGES_LIMIT,
		};

		// Load the conversation.
		return Api.getConversation(payload).then((res) => {
			// We failed :( give up.
			if (!res) {
				throw new Error(`Failed to load conversation with user ${userid}!`);
			}

			// Normalize the data.
			const conversation = normalizeConversationData(userid, res);

			// Pass the data to the store.
			dispatch({
				type: ADD_CONVERSATION,
				payload: {
					userid,
					conversation,
				},
			});
		});
	};
}

// Opens a conversation, overflowing all others.
export function openConversation(unsafeUserid, params = {}) {
	const userid = `${unsafeUserid}`;

	return (dispatch, getState) => {
		// We wanna check a few extra things.
		const { messagePledge } = getState().globalMessaging;

		// Don't let them send if they don't have photos.
		if (Context.has("NO_PHOTOS")) {
			showPhotoBlock();

			return;
		}

		// Don't let them send until they understand the message pledge.
		if (messagePledge && !messagePledge.understands) {
			showMessagePledge(userid, messagePledge, () => {
				dispatch(graduateMessagePledge());
				dispatch(openConversation(userid));

				Api.graduateMessagePledge().catch((error) => {
					console.error(error);
				});
			});

			return;
		}

		const conversation = getState().globalMessaging.conversationData[userid];

		// This conversation hasn't been loaded yet!
		// Load it, and come back later.
		if (!conversation) {
			dispatch(loadConversation(userid)).then(() => {
				dispatch(openConversation(userid, params));
			});

			return;
		}

		const {
			profileComment,
			likedThisSession,
		} = params;

		const user = conversation.fields;
		const theyIntrod = !!(
			user.last_contacts &&
			user.last_contacts.threadid &&
			user.first_message &&
			user.first_message.body
		);

		const shouldIgnoreProfileComment = (
			likedThisSession &&
			theyIntrod
		);

		// If the conversation was not open...
		if (conversation.state !== WINDOW_STATE.Open) {
			// Set conversation to open.
			dispatch({
				type: OPEN_CONVERSATION,
				payload: { userid, likedThisSession },
			});

			// Persist this change.
			dispatch(persistMessengerState());

			// Mark messages as read.
			dispatch(markConversationMessagesRead(userid));
		}

		// Add the comment to the active draft.
		if (!shouldIgnoreProfileComment && profileComment) {
			dispatch(updateDraft(
				userid,
				conversation.draft ? conversation.draft.body : "",
				profileComment,
			));
		}
	};
}

// Minimize conversation.
export function minimizeConversation(unsafeUserid) {
	const userid = `${unsafeUserid}`;

	return (dispatch) => {
		dispatch({
			type: MINIMIZE_CONVERSATION,
			payload: {
				userid,
			},
		});

		// Persist this change.
		dispatch(persistMessengerState());
	};
}

// User closed out a message conversation.
export function closeConversation(unsafeUserid) {
	const userid = `${unsafeUserid}`;

	return (dispatch, getState) => {
		const conversation = getState().globalMessaging.conversationData[userid];

		// Nothing to do here.
		if (!userid || !conversation) {
			return;
		}

		dispatch({
			type: CLOSE_CONVERSATION,
			payload: { userid },
		});

		// Persist this change.
		dispatch(persistMessengerState());
	};
}

// User typed into message composer.
export function updateDraft(userid, body, profileComment) {
	return (dispatch, getState) => {
		const conversation = getState().globalMessaging.conversationData[userid];

		// Can't do anything if there's no loaded conversation.
		if (!conversation) {
			return;
		}

		const nothingChanged = conversation.draft
			? conversation.draft.body === body && isEqual(
				conversation.draft.profileComment,
				profileComment,
			)
			: !body && !profileComment;

		// If nothing changed, no reason to proceed.
		if (nothingChanged) {
			return;
		}

		// Update state.
		dispatch({
			type: UPDATE_DRAFT,
			payload: {
				userid,
				body,
				profileComment,
			},
		});

		// Save the draft if it exists.
		const draftExists = body || profileComment;

		// Persist draft.
		if (draftExists) {
			// Can't persist if our drafts are full.
			const draftsFull = getState().globalMessaging.draftsFull;

			if (!draftsFull) {
				// Save the current draft. Throttled.
				Api.throttleSaveMessageDraft({
					userid,
					message: body,
					profileComment,
				}).catch((err) => {
					// Mark drafts full as needed.
					dispatch({
						type: UPDATE_DRAFT_FAIL,
						payload: {
							draftsFull: err && err.message === "Your drafts are full",
						},
					});
				});
			}
		} else {
			// Cancel any throttled instances.
			const throttledSaveMessageDraft = Api.getThrottledSaveMessageDraft();

			if (throttledSaveMessageDraft) {
				throttledSaveMessageDraft.cancel();
			}

			// Delete.
			Api.deleteMessageDraft({ userid });
		}
	};
}

// User wants to send a message.
export function sendMessage(userid) {
	return (dispatch, getState) => {
		// Grab a copy of the current conversation info.
		const conversation = getState().globalMessaging.conversationData[userid];

		// No draft, exit.
		if (!conversation.draft || !conversation.draft.body || !conversation.draft.body.trim()) {
			return;
		}

		const comment = conversation.draft.profileComment;

		const messageText = conversation.draft.body.trim();

		// Temporary message data to patch in this message while we send.
		const currentUserId = Context.get("userid");
		const tmpId = generateTemporaryMessageId();
		const timestamp = generateTimestamp();
		const service = Context.get("service");
		const nway = conversation.nway;

		// Placeholder.
		const mockMessage = {
			tmpId,
			timestamp,

			from: currentUserId,
			body: messageText,
			profileComment: comment,
		};

		// Kickoff sending the message.
		dispatch({
			type: SEND_MESSAGE_START,
			payload: {
				userid,
				message: mockMessage,
			},
		});

		// Clear out the draft.
		dispatch(updateDraft(userid, "", null));

		const sendAsProfileComment = (
			comment &&
			comment.comment_type &&
			comment.comment_content
		);

		// Construct the payload.
		const data = {
			senderid: currentUserId,
			receiverid: userid,
			body: messageText,
			profile_comment: sendAsProfileComment ? {
				comment_type: comment.comment_type,
				comment_content: comment.comment_content,
			} : undefined,

			source: "desktop_global",
			service,
		};

		// Do the thing!
		Api.sendMessage(data)
			.then((res) =>
				dispatch(sendMessageSuccess(userid, tmpId, messageText, conversation.draft, res)),
			)
			.catch((res) => dispatch(sendMessageFail(userid, tmpId, messageText, comment, nway, res)));
	};
}

// Message send succeeded.
export function sendMessageSuccess(userid, tmpId, messageText, draft, res) {
	return (dispatch, getState) => {
		const conversation = getState().globalMessaging.conversationData[userid];
		const totalMessages = conversation.totalMessages;
		const matchPercentage = conversation.fields.percentages.match;

		const { msgid, extras: { userInstructions } = {} } = res;

		// Drop a message length stat, if first contact.
		// TODO: Move this stat to API layer.
		if (!totalMessages) {
			const name = process.env.PLATFORM === "mobile"
				? "mobile site - global messaging - new contact - length"
				: "desktop - global messaging - new contact - length";
			const hash = "3pyVRIUDWDy6FAITGYoKyYPEYNg=";
			window.util.updateStats(name, messageText.length, "value", hash);
		}

		return dispatch({
			type: SEND_MESSAGE_SUCCESS,
			payload: {
				userid,
				tmpId,
				msgid,
				userInstructions,

				// This stuff is passed for analytics purposes.
				matchPercentage,
				messageText,
				draft,
			},
		});
	};
}

// Message send failed.
let sendFailTimeout = null;
export function sendMessageFail(userid, tmpId, messageText, profileComment, nway, res) {
	// Extract the error message.
	const error = res && res.error && res.error.message && res.error.message !== ERROR_MESSAGE_DEFAULT
		? _str(res.error.message)
		: _str("Message failed to send 😢");

	return (dispatch) => {
		// Update error message.
		dispatch({
			type: SEND_MESSAGE_FAIL,
			payload: {
				userid,
				tmpId,
				error,
				nwayBeforeSend: nway,
			},
		});

		// Restore the draft.
		dispatch(updateDraft(userid, messageText, profileComment));

		// Set timeout to remove it in 3000s.
		clearTimeout(sendFailTimeout);
		sendFailTimeout = setTimeout(() => {
			dispatch({
				type: SEND_MESSAGE_FAIL_REMOVE_ERROR,
				payload: {
					userid,
				},
			});
		}, ERROR_MESSAGE_DURATION);
	};
}

// Add a new message to a conversation.
export function addMessage(userid, {
	id,
	from,
	to,
	body,
	timestamp,
	profile_comment: profileComment,
}) {
	return {
		type: ADD_MESSAGE,
		payload: {
			userid,
			message: {
				id,
				body,
				profileComment,
				timestamp,
				from,
				to,
			},
		},
	};
}

// Mark a message in a conversation as read.
export function addReadReceipt(userid, msgid) {
	return {
		type: ADD_READ_RECEIPT,
		payload: {
			userid,
			msgid,
		},
	};
}

// User wants to unmatch this user.
export function onUnmatch(userid) {
	return () => {
		ProfileActions.unmatch(userid);
	};
}

// User wants to block this user.
export function onBlock(userid) {
	return (dispatch, getState) => {
		const {
			fields: user,
		} = getState().globalMessaging.conversationData[userid];

		ProfileActions.block(userid, {
			matchPercentage: user.percentages ? user.percentages.match : undefined,
		});
	};
}

// User wants to unblock this user.
export function onUnblock(userid) {
	return (dispatch) => {
		ProfileActions.unblock(userid);

		dispatch({
			type: UNBLOCK_USER,
			payload: {
				userid,
			},
		});
	};
}

// User wants to report this user.
export function onReport(userid, fromContentWarning) {
	return (dispatch, getState) => {
		const {
			threadid,
			fields: user,
			messages = [],
		} = getState().globalMessaging.conversationData[userid];

		const lastMessage = getLastMessageFromUser(userid, messages);

		const payload = {
			type: TYPES.MESSAGE,
			threadid,
			userid: user.userid,
			username: user.username,
			displayname: user.userinfo.displayname,
			msgid: lastMessage ? lastMessage.id : null,
			matchPercentage: user.percentages ? user.percentages.match : undefined,
			fromContentWarning,
		};

		Report.open(payload);
	};
}

// User wants to cancel content warning.
export function onDismissContentWarning(userid) {
	return (dispatch) => {
		Api.cancelContentWarning({ userid }).then(() => {
			dispatch({
				type: DISMISS_CONTENT_WARNING,
				payload: {
					userid,
				},
			});
		});
	};
}

// Accept the requested profile link.
export function onProfileLinkAccept(tuid) {
	ProfileActions.link(tuid);

	return {
		type: PROFILE_LINK_ACCEPT,
		payload: {
			tuid,
		},
	};
}

// Reject the requested profile link.
export function onProfileLinkReject(tuid, timestamp) {
	ProfileActions.linkReject(tuid, timestamp);

	return {
		type: PROFILE_LINK_REJECT,
		payload: {
			tuid,
		},
	};
}

// Page is visible.
export function onPageVisible() {
	return (dispatch, getState) => {
		dispatch({
			type: SET_PAGE_VISIBLE,
		});

		const conversationList = isStandalonePage || isMobile
			? [GetUserIdFromWindowLocation()]
			: getState().globalMessaging.conversationOrder;

		// Find open windows and mark them as read.
		conversationList
			.map((userid) => getState().globalMessaging.conversationData[userid])
			.filter((conversation) => conversation && isConversationActive(conversation))
			.forEach((conversation) => (
				dispatch(markConversationMessagesRead(conversation.fields.userid))
			));
	};
}

// Page was hidden.
export function onPageHidden() {
	return {
		type: SET_PAGE_HIDDEN,
	};
}

// Start phone verification flow if needed, or just move the convo to SMS..
export function onMoveToSMS(otherUserId) {
	return (dispatch, getState) => {
		// Does this user need to verify their phone first?
		const { needsPhoneVerification } = getState().globalMessaging;

		// Go through phone verification flow—it'll handle the SMS forwarding.
		if (needsPhoneVerification) {
			return PhoneVerificationModal.open({
				otherUserId,
				onComplete: () => {
					dispatch({
						type: MOVE_TO_SMS_SUCCESS,
						payload: {
							userid: otherUserId,
						},
					});

					PhoneVerificationModal.close();
				},
			});
		}

		// Grab a copy of the current conversation info.
		const conversation = getState().globalMessaging.conversationData[otherUserId];

		// Hmm, we shouldn't be showing them the option. Give up.
		if (!conversation.showMoveToSMS) {
			return null;
		}

		// Awesome, we can just start the forwarding directly.
		dispatch({
			type: MOVE_TO_SMS_START,
			payload: {
				userid: otherUserId,
			},
		});
		return Api.startSMSForwarding(otherUserId).then((results) => {
			if (results.success) {
				dispatch({
					type: MOVE_TO_SMS_SUCCESS,
					payload: {
						userid: otherUserId,
					},
				});
			} else {
				dispatch({
					type: MOVE_TO_SMS_FAIL,
					payload: {
						userid: otherUserId,
					},
				});
			}
		}).catch(() => {
			dispatch({
				type: MOVE_TO_SMS_FAIL,
				payload: {
					userid: otherUserId,
				},
			});
		});
	};
}

// Persist preferred send trigger.
export function onChangePreferredSendTrigger(sendTrigger) {
	return (dispatch) => {
		dispatch({
			type: SET_PREFERRED_SEND_TRIGGER,
			payload: {
				preferredSendTrigger: sendTrigger,
			},
		});
		dispatch(persistMessengerState());
	};
}

export function graduateMessagePledge() {
	return { type: GRADUATE_MESSAGE_PLEDGE };
}



// WEBPACK FOOTER //
// ./src/ducks/global_messaging.js